import React from 'react'
import Page from 'components/Page'
import { defineMessages } from 'react-intl'
import useIntl from 'modules/component/utils/hooks/useIntl'
import formatMessageIntlUtil from 'modules/intl/utils/format-message'
import useSelector from 'modules/component/utils/hooks/useSelector'
import { getByIdExerciseSelector, loadByIdExerciseAction, EXERCISE_KEY, getByIdMediaSelector, getVideoMediaIdByExerciseIdSelector, MEDIA_MODEL_KEY } from '@gymaholic/packages'
import getValueReduxUtil from 'modules/redux/utils/get-value'
import { View } from 'components/View'
import Block from 'components/Block'
import TextTitleCompressedHeavy from 'components/TextTitleCompressedHeavy'
import TextBodySmaller from 'components/TextBodySmaller'
import ButtonPrimary from 'components/ButtonPrimary'
import { ROUTE_PATH } from 'modules/routes/constants/index.cjs'
import pushRouterAction from 'modules/router/actions/push'
import useDispatch from 'modules/component/utils/hooks/useDispatch'
import { useTheme } from 'modules/styles'
import { isMobileScreenViewportSelect } from 'modules/viewport/selectors'
import { ExerciseVideo } from 'pages/Exercise/components/Video'
import { ExerciseMuscles } from 'pages/Exercise/components/Muscles'
import { ExerciseEquipment } from 'pages/Exercise/components/Equipment'
import { HowToPerform } from './HowToPerform'
import { CommonMistakes } from './CommonMistakes'
import { WhyFormMatters } from './WhyFormMatters'
import { CheckExerciseFormSlugFAQ } from './FAQ'
import { getDetailsByIdExerciseSelector } from 'modules/exercise/redux/selectors/get-details-by-id'

const messages = defineMessages({
  title: {
    id: 'CheckExerciseFormSlugPage.title',
    defaultMessage: 'Check Your {exerciseName} Form With AI | Gymaholic Form Checker'
  },
  metaDescription: {
    id: 'CheckExerciseFormSlugPage.metaDescription',
    defaultMessage: 'Upload your {exerciseName} video and get instant AI feedback on your posture, rhythm, and control. Improve your technique and avoid common mistakes with smart analysis.'
  },
  headline: {
    id: 'CheckExerciseFormSlugPage.headline',
    defaultMessage: 'Check Your {exerciseName} Form With AI'
  },
  subheadline: {
    id: 'CheckExerciseFormSlugPage.subheadline',
    defaultMessage: 'Upload your video and get instant feedback on your {exerciseName} posture, rhythm, and coordination.'
  },
  ctaText: {
    id: 'CheckExerciseFormSlugPage.ctaText',
    defaultMessage: 'Check Your Form'
  },
  howItWorksHeading: {
    id: 'CheckExerciseFormSlugPage.howItWorksHeading',
    defaultMessage: 'How It Works'
  },
  techniqueHeading: {
    id: 'CheckExerciseFormSlugPage.techniqueHeading',
    defaultMessage: 'How to Know if Your {exerciseName} Form Is Correct'
  },
  howToDoHeading: {
    id: 'CheckExerciseFormSlugPage.howToDoHeading',
    defaultMessage: 'How to Perform {exerciseName}'
  }
})

export default function CheckExerciseFormSlugPage (props) {
  const intl = useIntl()
  const dispatch = useDispatch()
  const theme = useTheme()
  const exerciseId = Number(props.match?.params?.id)
  const exercise = useSelector(getByIdExerciseSelector(exerciseId))
  const isMobileScreen = useSelector(isMobileScreenViewportSelect)

  const exerciseName = getValueReduxUtil(exercise, EXERCISE_KEY.NAME) || ''
  const muscles = getValueReduxUtil(exercise, EXERCISE_KEY.MUSCLES) || []
  const equipment = getValueReduxUtil(exercise, EXERCISE_KEY.EQUIPMENT) || []

  const mediaVideoId = useSelector(getVideoMediaIdByExerciseIdSelector(exerciseId))
  const media = useSelector(getByIdMediaSelector(mediaVideoId))
  const posterUrl = getValueReduxUtil(media, MEDIA_MODEL_KEY.THUMBNAIL)
  const videoUrl = getValueReduxUtil(media, MEDIA_MODEL_KEY.URL)

  const exerciseDetails = useSelector(getDetailsByIdExerciseSelector(exerciseId))
  const details = exerciseDetails?.[0] || {}

  React.useEffect(() => {
    if (exerciseId && !exercise) {
      dispatch(loadByIdExerciseAction(exerciseId))
    }
  }, [exerciseId, exercise, dispatch])

  React.useEffect(() => {
    if (exerciseDetails) {
      console.log('[CheckExerciseFormSlug] exerciseDetails:', exerciseDetails)
      console.log('[CheckExerciseFormSlug] details:', details)
      console.log('[CheckExerciseFormSlug] commonFormMistakes:', details.commonFormMistakes)
      console.log('[CheckExerciseFormSlug] formCheckingTips:', details.formCheckingTips)
      console.log('[CheckExerciseFormSlug] whyFormMatters:', details.whyFormMatters)
      console.log('[CheckExerciseFormSlug] faqs:', details.faqs)
    }
  }, [exerciseDetails, details])

  const title = formatMessageIntlUtil(intl, messages.title, { exerciseName })
  const metaDescription = formatMessageIntlUtil(intl, messages.metaDescription, { exerciseName })

  const handleCTAClick = () => {
    dispatch(pushRouterAction({
      pathname: ROUTE_PATH.TOOLS_CHECK_EXERCISE_FORM,
      search: `?exerciseId=${exerciseId}&source=CHECK_YOUR_EXERCISE_FORM_PAGE`
    }))
  }

  return (
    <Page
      shouldNotHavePadding
      title={title}
      metaDescription={metaDescription}
    >
      {/* Hero Section */}
      <Block
        bgc='white'
        style={{ textAlign: 'center' }}
      >
        <View
          centered
          style={{
            maxWidth: theme.maxWidth.desktopCompact
          }}
        >
          <View mb={2}>
            <TextTitleCompressedHeavy>
              {formatMessageIntlUtil(intl, messages.headline, { exerciseName })}
            </TextTitleCompressedHeavy>
          </View>

          <View mb={3}>
            <TextBodySmaller
              style={{ color: theme.colors.paragraph }}
            >
              {formatMessageIntlUtil(intl, messages.subheadline, { exerciseName })}
            </TextBodySmaller>
          </View>

          <ButtonPrimary onClick={handleCTAClick}>
            {formatMessageIntlUtil(intl, messages.ctaText)}
          </ButtonPrimary>
        </View>
      </Block>

      {/* Exercise Video Section */}
      {videoUrl && posterUrl && (
        <Block bgc='backgroundContent'>
          <View centered>
            <ExerciseVideo
              videoUrl={videoUrl}
              posterUrl={posterUrl}
              isMobileScreen={isMobileScreen}
              shouldAutoPlay={false}
            />
          </View>
        </Block>
      )}

      {/* Exercise Details - Muscles and Equipment */}
      {(muscles.length > 0 || equipment.length > 0) && (
        <Block bgc='white'>
          <View
            centered
            style={{
              maxWidth: theme.maxWidth.desktopCompact,
              textAlign: 'center'
            }}
          >
            {muscles.length > 0 && (
              <ExerciseMuscles
                muscles={muscles}
                exercise={exercise}
              />
            )}
            {equipment.length > 0 && (
              <ExerciseEquipment
                equipment={equipment}
              />
            )}
          </View>
        </Block>
      )}

      {/* How to Perform Section */}
      <HowToPerform exerciseName={exerciseName} content={details.commonFormMistakes} />

      {/* Common Mistakes Section */}
      <CommonMistakes exerciseName={exerciseName} content={details.formCheckingTips} />

      {/* Why Form Matters Section */}
      <WhyFormMatters exerciseName={exerciseName} content={details.whyFormMatters} />

      {/* How It Works Section */}
      <Block
        bgc='backgroundContent'
      >
        <View
          centered
          style={{
            maxWidth: theme.maxWidth.desktopCompact
          }}
        >
          <View mb={3}>
            <TextTitleCompressedHeavy style={{ textAlign: 'center' }}>
              {formatMessageIntlUtil(intl, messages.howItWorksHeading)}
            </TextTitleCompressedHeavy>
          </View>

          <View mb>
            <TextBodySmaller style={{ color: theme.colors.paragraph }}>
              1. Upload your video - capture your {exerciseName} clearly from a 45-degree angle.
            </TextBodySmaller>
          </View>
          <View mb>
            <TextBodySmaller style={{ color: theme.colors.paragraph }}>
              2. AI analyzes your movement - evaluates coordination, tempo, and posture.
            </TextBodySmaller>
          </View>
          <View mb>
            <TextBodySmaller style={{ color: theme.colors.paragraph }}>
              3. Get actionable feedback - see your performance score and improvements.
            </TextBodySmaller>
          </View>
          <View>
            <TextBodySmaller style={{ color: theme.colors.paragraph }}>
              4. Train efficiently - refine your technique with every session.
            </TextBodySmaller>
          </View>
        </View>
      </Block>

      {/* FAQ Section */}
      {details.faqs && details.faqs.length > 0 && (
        <CheckExerciseFormSlugFAQ faqs={details.faqs} />
      )}

      {/* Final CTA Section */}
      <Block
        bgc='white'
        style={{ textAlign: 'center' }}
      >
        <View centered>
          <ButtonPrimary onClick={handleCTAClick}>
            {formatMessageIntlUtil(intl, messages.ctaText)}
          </ButtonPrimary>
        </View>
      </Block>
    </Page>
  )
}
